/**
 * Which file is a game's card artwork.
 *
 * Kept apart from the component because it is the only part of this with rules
 * worth stating. The bundler hands over whatever is in the artwork folder as a
 * map of path to URL, in an order nobody promises — so picking "the first
 * match" would quietly depend on that order the day somebody leaves both a
 * .png and a .webp of the same game behind.
 */
export const ARTWORK_TYPES = ['webp', 'png', 'jpg', 'svg'] as const

/** The name a file claims, e.g. "../assets/games/hangman.webp" -> "hangman". */
function gameOf(path: string): string {
  return (
    path
      .split('/')
      .pop()
      ?.replace(/\.[^.]+$/, '') ?? ''
  )
}

function typeOf(path: string): string {
  return path.split('.').pop()?.toLowerCase() ?? ''
}

/**
 * The artwork for one game, or nothing.
 *
 * With more than one file for the same game, the best format wins rather than
 * the luckiest: WebP first, because it is a tenth the size of the PNG it
 * usually replaces, and the old file being left behind is exactly what happens
 * when somebody adds a better one.
 */
export function pickArtwork(
  files: Readonly<Record<string, string>>,
  gameKey: string,
): string | undefined {
  const mine = Object.entries(files).filter(([path]) => gameOf(path) === gameKey)
  if (mine.length === 0) return undefined

  const best = [...mine].sort(([a], [b]) => {
    const rank = (path: string): number => {
      const at = ARTWORK_TYPES.indexOf(typeOf(path) as (typeof ARTWORK_TYPES)[number])
      // A type nobody listed sorts last rather than first.
      return at === -1 ? ARTWORK_TYPES.length : at
    }

    // Ranked by format, then by name, so the answer never depends on the order
    // the bundler happened to hand them over in.
    return rank(a) - rank(b) || a.localeCompare(b)
  })

  return best[0]?.[1]
}
